import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useModalA11y } from "../hooks/useModalA11y.js";
import { useSheetDrag } from "./useSheetDrag.js";
import { useCloseGuard, type DialogDirty } from "../CloseGuard/CloseGuard.js";
import styles from "./Sheet.module.scss";

export interface SheetProps {
  /** Accessible dialog name (the sheet has no visible header — the caller draws its own content). */
  title: string;
  onClose: () => void;
  /**
   * WARM MODE. Omit it (the default) and the sheet's mere presence means "open" — the caller conditionally
   * mounts it, and it plays its slide-up on mount. Pass a boolean and the sheet stays MOUNTED across the
   * toggle so opening is a CSS state flip with no React mount cost — the "preload so it opens instantly"
   * shape for a sheet whose body is already in memory (the More sheet). While `open={false}` the a11y hook
   * is inert (no Escape slot, no focus trap, no scroll lock) and the panel is `visibility:hidden`, so it is
   * neither focusable nor interactive — a closed-but-mounted sheet behaves exactly like an unmounted one.
   */
  open?: boolean;
  /** aria-label for the scrim (default "Close"). */
  closeLabel?: string;
  /** Extra class on the sheet panel. */
  className?: string;
  /**
   * A skin hook for the BACKDROP — its scrim colour, blur and entrance — for an app whose sheet must look like its own
   * product's (Antenna's legacy checkout sheet: a 50% ink scrim blurred 3px, R-9). Modal's `backdropClassName` (A-58),
   * on Sheet. The backdrop's mechanics stay the primitive's: it is still fixed, lifted above the soft keyboard and on
   * the drawer layer, and the scrim button inside it still closes. Additive — absent, nothing changes.
   */
  backdropClassName?: string;
  /**
   * A skin hook for the decorative drag HANDLE (it stays `aria-hidden`). When passed, the handle also carries an empty
   * `data-sheet-grab` attribute — a hook for a visual harness, which has no role or text to find a decorative box by.
   * Additive — absent, the handle's DOM is unchanged.
   */
  handleClassName?: string;
  /**
   * Drag the panel DOWN to dismiss — TOUCH only, armed only when a touch lands while the panel's own scroll is at its
   * top, so scrolling the body never fights the gesture (Antenna `sheet-swipe 2026-07-12`). A release past
   * `dragDismissDistance` calls `onClose`; a shorter one settles back. The scrim and Escape stay the single-pointer and
   * keyboard alternatives (WCAG 2.5.7). ON by default (PO, 2026-10-04): the grabber promises the gesture, so it
   * performs it; it is inert while a warm sheet is closed. It goes through the same close request as every other exit,
   * so a sheet with edits asks before discarding them and the panel settles back if they are kept.
   */
  dragToDismiss?: boolean;
  /**
   * Unsaved edits in the sheet — `true`/`false`, or `"auto"` to infer them from the user typing into a field inside it
   * (see `useCloseGuard`). With edits, every dismissal (grabber, scrim, Escape, a `DialogCancel`) asks "Discard
   * changes?" first. Absent ⇒ never asks.
   */
  dirty?: DialogDirty;
  /**
   * Where the body scrolls. `panel` (default) scrolls the whole panel. `inner` holds the panel still, so the caller's
   * own pinned header and footer stay put, and the caller's content fills the panel (a flex column) with its own
   * scrolling region inside. `FormDialog` uses it so a form's title and actions never scroll away.
   */
  scroll?: "panel" | "inner";
  /**
   * Lift the sheet above the modal and dialog layers. A sheet's own layer is the drawer (300), under `Modal` (400) and
   * `AcDialog`/`ActionModal` (500) — right for a sheet that IS the surface, wrong for one opened FROM a dialog, which
   * would slide up behind it. `Picker`'s touch sheet passes this (ART-266).
   */
  stacked?: boolean;
  /** How far, in px, a released drag must EXCEED to dismiss (default 110 — legacy's `dragY > 110`). */
  dragDismissDistance?: number;
  children: ReactNode;
}

/**
 * The bottom-sheet primitive — the mobile-native overlay: a panel anchored to the bottom edge
 * with a drag handle, sliding up over a tap-to-dismiss scrim. Chrome only (scrim + panel + handle + entrance);
 * the body is whatever the caller passes as `children`, so each consumer keeps its own content/list styling.
 * Folds in `useModalA11y` (topmost-only Escape so a sheet stacked over a modal unwinds one layer at a time,
 * ref-counted body-scroll freeze, focus trap + restore) — the SAME tested a11y core as `Modal`/`AcDialog`.
 * Promoted at to back the MobileShell More sheet + the account/band-switcher sheet (the two consumers
 * that drove the promotion). Caps to the viewport so a tall body scrolls internally. An app whose sheet must look like
 * its own product's skins the backdrop, panel and handle through their class hooks and may opt into drag-to-dismiss
 * (Antenna's fan sheet, A-77); a sheet passing none of them renders exactly as it always has.
 */
export function Sheet({
  title,
  onClose,
  open,
  closeLabel = "Close",
  className,
  backdropClassName,
  handleClassName,
  dragToDismiss = true,
  dragDismissDistance,
  dirty,
  scroll = "panel",
  stacked = false,
  children,
}: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const controlled = open !== undefined;
  const isOpen = open ?? true;
  const guard = useCloseGuard({ onClose, dirty, root: sheetRef });
  useModalA11y(sheetRef, guard.requestClose, isOpen);
  useSheetDrag(sheetRef, {
    enabled: dragToDismiss && isOpen,
    distance: dragDismissDistance ?? 110,
    onDismiss: guard.requestClose,
  });

  // Uncontrolled: no data-state, so the CSS keyframe entrance plays on mount as before. Controlled: a
  // data-state attribute the stylesheet transitions between, and `visibility:hidden` on close takes the
  // whole subtree out of the tab order and pointer path with no `inert` needed.
  const state = controlled ? (isOpen ? "open" : "closed") : undefined;

  // PORTALLED to <body> (ART-251). Rendered in place, a sheet opened from inside the scrolling page content was
  // trapped in that content's stacking context, so the mobile tab bar (a fixed sibling OUTSIDE it, z-sticky)
  // painted over the sheet's bottom — a task's status row and actions sat hidden behind the bar. A portal is the
  // containing-block rule this codebase already applies to overlays mounted inside chrome (the inbox, Picker).
  // The theme rides <html>'s data-theme, so a portalled sheet still picks up DOS's dark palette.
  const target = typeof document !== "undefined" ? document.body : null;
  const node = (
    <div
      className={clsx(styles.backdrop, backdropClassName)}
      data-state={state}
      data-stacked={stacked ? "" : undefined}
      aria-hidden={controlled && !isOpen ? true : undefined}
    >
      {/* A real <button> scrim so click-outside-to-close is keyboard/AT-reachable (jsx-a11y); Escape (via
          useModalA11y) covers the same intent. tabIndex -1 keeps it out of the trap's tab order. */}
      <button
        type="button"
        className={styles.scrim}
        aria-label={closeLabel}
        tabIndex={-1}
        onClick={guard.requestClose}
      />
      <div
        ref={sheetRef}
        className={clsx(styles.sheet, className)}
        data-state={state}
        data-scroll={scroll === "inner" ? "inner" : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div
          className={clsx(styles.handle, handleClassName)}
          data-sheet-grab={handleClassName ? "" : undefined}
          aria-hidden="true"
        />
        {guard.provide(children)}
      </div>
      {guard.confirm}
    </div>
  );
  return target ? createPortal(node, target) : node;
}
